import React, { createContext, useContext, useState, useEffect } from "react";
import ReuseableSnackbar from "../components/Snackbar/Snackbar";

type SnackbarColor = "success" | "error" | "warning" | "info";

type SnackbarContextType = {
  showSnackbar: (message: string, color?: SnackbarColor) => void;
};

const GlobalSnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
);

type SnackbarPayload = {
  message: string;
  color?: SnackbarColor;
};

let externalSnackbarTrigger: ((payload: SnackbarPayload) => void) | null = null;

export const setSnackbarExternalTrigger = (
  callback: (payload: SnackbarPayload) => void
) => {
  externalSnackbarTrigger = callback;
};

export const triggerGlobalSnackbar = (payload: SnackbarPayload) => {
  if (externalSnackbarTrigger) {
    externalSnackbarTrigger(payload);
  }
};

export const GlobalSnackbarProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [color, setColor] = useState<SnackbarColor>("error");

  const showSnackbar = (msg: string, clr: SnackbarColor = "error") => {
    setMessage(msg);
    setColor(clr);
    setIsOpen(true);
  };

  useEffect(() => {
    setSnackbarExternalTrigger(({ message, color }) => {
      showSnackbar(message, color || "error");
    });
  }, []);

  return (
    <GlobalSnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <ReuseableSnackbar
        isShowSnackbar={isOpen}
        setIsShowSnackbar={setIsOpen}
        colour={color}
        message={message}
        autoHideDuration={3000}
      />
    </GlobalSnackbarContext.Provider>
  );
};

export const useGlobalSnackbar = () => {
  const context = useContext(GlobalSnackbarContext);
  if (!context) {
    throw new Error(
      "useGlobalSnackbar must be used within a GlobalSnackbarProvider"
    );
  }
  return context;
};


